// Recursively walks the FileSystemEntry tree you get from a drag-and-drop
// event so that dropping a folder behaves the same as picking one via
// <input webkitdirectory>. Every returned File has a `relativePath`
// property set (folder1/folder2/file.pdf), mirroring `webkitRelativePath`.

const isHiddenName = (name) => name.startsWith('.');

const readEntryAsFile = (entry) =>
  new Promise((resolve) => {
    entry.file((file) => {
      Object.defineProperty(file, 'relativePath', {
        value: entry.fullPath.replace(/^\//, ''),
        writable: false,
      });
      resolve(file);
    });
  });

const readDirectoryEntries = (dirReader) =>
  new Promise((resolve, reject) => {
    const allEntries = [];

    const readBatch = () => {
      dirReader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(allEntries);
          return;
        }
        allEntries.push(...batch);
        readBatch();
      }, reject);
    };

    readBatch();
  });

const walkEntry = async (entry, files) => {
  if (!entry) return;

  // Skip hidden files/folders entirely (.DS_Store, .git, .thumbnails, etc.)
  // — don't even descend into hidden directories.
  if (isHiddenName(entry.name)) return;

  if (entry.isFile) {
    files.push(await readEntryAsFile(entry));
    return;
  }

  if (entry.isDirectory) {
    const dirReader = entry.createReader();
    const entries = await readDirectoryEntries(dirReader);
    await Promise.all(entries.map((child) => walkEntry(child, files)));
  }
};

// items = event.dataTransfer.items
export const getFilesFromDataTransferItems = async (items) => {
  const entries = Array.from(items)
    .map((item) => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
    .filter(Boolean);

  const files = [];
  await Promise.all(entries.map((entry) => walkEntry(entry, files)));
  return files;
};

// Normalizes either drag-drop files (relativePath) or <input webkitdirectory>
// files (webkitRelativePath) into { file, relativePath }.
// relativePath here is the *folder only* (filename stripped), '' if top-level.
export const withRelativePath = (file) => {
  const rawPath = file.relativePath || file.webkitRelativePath || file.name;
  const parts = rawPath.split('/');
  parts.pop(); // drop the filename itself
  return {
    file,
    relativePath: parts.join('/'),
  };
};

// True if the file itself, or any folder in its path, is hidden
// (starts with a dot) — covers files picked via <input webkitdirectory>,
// which don't go through the entry-based walk above and so wouldn't
// otherwise be filtered.
export const isHiddenFile = (file) => {
  const rawPath = file.relativePath || file.webkitRelativePath || file.name;
  return rawPath.split('/').some((segment) => segment.startsWith('.'));
};
